import { useCallback, useLayoutEffect, useMemo, useState } from 'react';
import { graphql, useStaticQuery } from 'gatsby';
import { useMergePrismicPreviewData } from 'gatsby-plugin-prismic-previews';
import { useLocation } from '@reach/router';

import { HeaderLinks, HeaderListLinks } from './types';
import { HEADER_LINK_TYPE } from './constants';
import { ILanguageLinks } from './LanguageLinks';

import { ILanguages } from '../types';

import { useScrollLock } from 'utils/hooks';
import { Normalize } from 'utils/types';
import { BASE_LANGUAGE } from 'utils/constants';

export const useHeader = ({ currentLanguage, alternateLanguages, fallbackUrls }: ILanguages) => {
  const [open, setOpen] = useState(false);
  const [lock, unlock] = useScrollLock();

  useLayoutEffect(() => {
    if (open) lock();
    else unlock();
  }, [open]);

  const closeModal = useCallback(() => setOpen(false), []);

  const toggleModal = useCallback(() => setOpen(open => !open), []);

  const { homeData, headerData, headerLogo } = useHeaderData();

  const headerLinks = useHeaderLinks({ currentLanguage, headerData });

  const homeUrl = useMemo(
    () =>
      homeData.edges.reduce((acm, { node }) => {
        return node.lang === currentLanguage ? node?.url ?? acm : acm;
      }, '/'),
    [homeData]
  );

  const languageProps = useHeaderLanguages({
    homeUrl,
    currentLanguage,
    alternateLanguages,
    fallbackUrls,
    homeData,
    headerData,
  });

  const headerLogoImage = headerLogo.childImageSharp.gatsbyImageData
    ? { image: headerLogo.childImageSharp.gatsbyImageData, alt: 'ShakespeareMusic Logo' }
    : null;

  return {
    open,
    closeModal,
    toggleModal,
    headerLinks,
    homeUrl,
    languageProps,
    headerLogoImage,
  };
};

const useHeaderLinks = ({
  currentLanguage,
  headerData,
}: {
  currentLanguage: ILanguages['currentLanguage'];
  headerData: Queries.HeaderQuery['headerData'];
}): HeaderLinks => {
  const linksMap = headerData.edges.reduce((acm, { node }) => {
    const language = node.lang;

    const links: HeaderLinks =
      node.data.links?.reduce((acm, link) => {
        if (link?.link_label && link.link_content) {
          const { link_label, link_content } = link;
          const { url, type, document } = link_content;

          if (type !== 'header_list') {
            if (!url) return acm;

            return [
              ...acm,
              {
                type: type ? HEADER_LINK_TYPE.INTERNAL : HEADER_LINK_TYPE.EXTERNAL,
                label: link_label,
                url,
              },
            ];
          } else {
            const { link, links } = document?.['data'] as Queries.HeaderListDataFragment['data'];
            if (!links) return acm;

            const headerListLinks: HeaderListLinks = links?.reduce((acm, listLink) => {
              if (!listLink?.link_content) return acm;

              const overridingLabel = listLink?.link_label ?? null;

              const { url, document } = listLink.link_content;

              const { data } = document as {
                data: { compact_title: string | null };
              };

              const defaultLabel = data.compact_title;

              if (!overridingLabel && !defaultLabel) return acm;

              return [
                ...acm,
                {
                  label: (overridingLabel ?? defaultLabel) as string,
                  url: url as string,
                },
              ];
            }, []);

            if (!headerListLinks.length) return acm;

            return [
              ...acm,
              {
                type: HEADER_LINK_TYPE.LIST,
                label: link_label,
                url: link?.url ?? null,
                links: headerListLinks,
              },
            ];
          }
        }

        return acm;
      }, []) ?? [];

    return { ...acm, [language]: links };
  }, {});

  return linksMap?.[currentLanguage] ?? linksMap[BASE_LANGUAGE];
};

const useHeaderLanguages = ({
  homeUrl,
  currentLanguage,
  alternateLanguages,
  fallbackUrls = [],
  homeData,
  headerData,
}: { homeUrl: string } & ILanguages &
  Normalize<Pick<Queries.HeaderQuery, 'homeData' | 'headerData'>>): ILanguageLinks => {
  const { pathname } = useLocation();

  const supportedLanguages = useMemo(
    () =>
      headerData.edges.map(({ node }) => {
        const { lang, data } = node;

        const { language_label, language_compact_label, language_flag } = data;

        const [compactLabelFallback] = lang.split('-');

        const compactLabel = language_compact_label
          ? language_compact_label
          : compactLabelFallback.toUpperCase();

        const label = language_label ? language_label : compactLabel;

        const { gatsbyImageData, alt, ...flagProps } = language_flag;

        return {
          language: lang,
          label,
          compactLabel,
          flagImage: gatsbyImageData
            ? {
                ...flagProps,
                image: gatsbyImageData,
                alt: alt ?? '',
              }
            : null,
        };
      }),
    [headerData]
  );

  const languageUrls = useMemo(
    () =>
      supportedLanguages.map(({ language }) => {
        if (language === currentLanguage) return pathname;

        const alternateLanguagePageIndex = alternateLanguages.findIndex(
          alternateLanguage => alternateLanguage.language === language
        );

        if (alternateLanguagePageIndex > -1)
          return alternateLanguages[alternateLanguagePageIndex].url;

        const normalizedFallbackUrls = fallbackUrls.filter(fallbackUrl => !!fallbackUrl[language]);

        if (normalizedFallbackUrls.length) {
          const [potentialFallbackUrl] = normalizedFallbackUrls;
          if (potentialFallbackUrl[language]) return potentialFallbackUrl[language];
        }

        return homeData.edges.reduce((acm, { node }) => {
          return node.lang === language ? node.url : acm;
        }, homeUrl);
      }),
    [supportedLanguages, currentLanguage, alternateLanguages, fallbackUrls, pathname, homeData]
  );

  return useMemo(() => {
    const languages = supportedLanguages.map((language, currentIndex) => ({
      ...language,
      url: languageUrls[currentIndex] ?? '/',
    }));

    const currentLanguageIndex = languages.findIndex(
      ({ language }) => currentLanguage === language
    );

    return {
      currentLanguageCompactLabel: languages[currentLanguageIndex].compactLabel,
      languages,
    };
  }, [supportedLanguages, languageUrls]);
};

const useHeaderData = () => {
  const staticLayoutData = useStaticQuery<Queries.HeaderQuery>(headerQuery);

  const { data } = useMergePrismicPreviewData(staticLayoutData);

  return data as Normalize<Queries.HeaderQuery>;
};

const headerQuery = graphql`
  query Header {
    homeData: allPrismicHome {
      edges {
        node {
          ...HomeNodeData
        }
      }
    }

    headerData: allPrismicHeader(sort: { fields: lang }) {
      edges {
        node {
          ...HeaderNodeData
        }
      }
    }

    headerLogo: file(relativePath: { eq: "smLogo.png" }) {
      childImageSharp {
        gatsbyImageData(placeholder: BLURRED)
      }
    }
  }

  fragment HomeNodeData on PrismicHome {
    _previewable
    lang
    url
  }

  fragment HeaderNodeData on PrismicHeader {
    _previewable
    lang
    data {
      ...HeaderData
    }
  }

  fragment HeaderData on PrismicHeaderDataType {
    links {
      link_label
      link_content {
        url
        document {
          ... on PrismicHeaderList {
            ...HeaderListData
          }
        }
        type
      }
    }
    language_label
    language_compact_label
    language_flag {
      gatsbyImageData(placeholder: DOMINANT_COLOR)
      alt
    }
  }

  fragment HeaderListData on PrismicHeaderList {
    data {
      link {
        url
      }
      links {
        link_label
        link_content {
          url
          ...HeaderListLinksContentDocument
        }
      }
    }
  }

  fragment HeaderListLinksContentDocument on PrismicLinkType {
    document {
      ... on PrismicIndustry {
        data {
          compact_title
        }
      }
      ... on PrismicSolution {
        data {
          compact_title
        }
      }
      ... on PrismicBlogArticle {
        data {
          compact_title
        }
      }
    }
  }
`;
